/**
 * Drive Detection Background Task
 *
 * NOTE: expo-task-manager requires a native rebuild to enable background tracking.
 * Until the next build is submitted, this module stubs out the background functions
 * gracefully so the Drive Log screen works for manual trip entry via OTA.
 */

import * as Location from 'expo-location';
import { supabase } from '../lib/supabase';
import { haversineDistance } from '../hooks/useDriveLog';
import { setDriveActive, setTodayMiles, setTodayTrips } from '../lib/widgetBridge';

export const DRIVE_TASK_NAME = 'SLATE_DRIVE_DETECTION';

// Lazy-load native modules so OTA doesn't crash if they aren't in the binary
let TaskManager = null;
let AsyncStorage = null;

try {
  TaskManager = require('expo-task-manager');
} catch (_) {}

try {
  AsyncStorage = require('@react-native-async-storage/async-storage').default;
} catch (_) {}

// True only when the native TaskManager module is present in the current binary.
// Always false on OTA-only updates — background tracking requires a native rebuild.
export const isAutoDetectionAvailable = !!TaskManager;

const STORAGE_KEY = 'slate_drive_state';
const USER_ID_KEY = 'slate_drive_user_id';
const ENABLED_KEY = 'slate_drive_enabled';
const MIN_DRIVE_SPEED_MPH = 8;
const STOP_SPEED_MPH = 4;
const READINGS_TO_START = 2;
const READINGS_TO_STOP = 3;
const MIN_TRIP_MILES = 0.25;

function toMph(mps) {
  if (mps == null || mps < 0) return 0; // -1 means speed unavailable on iOS
  return mps * 2.23694;
}

async function reverseGeocode(lat, lng) {
  try {
    const results = await Location.reverseGeocodeAsync({ latitude: lat, longitude: lng });
    if (results?.[0]) {
      const r = results[0];
      return [r.name, r.city, r.region].filter(Boolean).join(', ');
    }
  } catch (_) {}
  return '';
}

async function loadState() {
  if (!AsyncStorage) return null;
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) { return null; }
}

async function saveState(state) {
  if (!AsyncStorage) return;
  try { await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (_) {}
}

async function clearState() {
  if (!AsyncStorage) return;
  try { await AsyncStorage.removeItem(STORAGE_KEY); } catch (_) {}
}

async function saveTrip(state) {
  try {
    // Read user_id from AsyncStorage — more reliable in background context than supabase.auth.getUser()
    let userId = null;
    if (AsyncStorage) {
      userId = await AsyncStorage.getItem(USER_ID_KEY);
    }
    if (!userId) {
      // Fallback to supabase auth
      const { data: { user } } = await supabase.auth.getUser();
      userId = user?.id;
    }
    if (!userId) return;
    const distance = haversineDistance(state.startLat, state.startLng, state.lastLat, state.lastLng);
    if (distance < MIN_TRIP_MILES) return;
    const [startAddress, endAddress] = await Promise.all([
      reverseGeocode(state.startLat, state.startLng),
      reverseGeocode(state.lastLat, state.lastLng),
    ]);
    await supabase.from('drive_logs').insert({
      user_id: userId,
      start_time: state.startTime,
      end_time: new Date().toISOString(),
      start_address: startAddress,
      end_address: endAddress,
      start_lat: state.startLat,
      start_lng: state.startLng,
      end_lat: state.lastLat,
      end_lng: state.lastLng,
      distance_miles: Math.round(distance * 100) / 100,
      purpose: 'business',
      is_auto: true,
    });
  } catch (err) {
    console.warn('[DriveDetection] Failed to save trip:', err.message);
  }
}

// Register task only if TaskManager native module is available
if (TaskManager) {
  TaskManager.defineTask(DRIVE_TASK_NAME, async ({ data, error }) => {
    if (error) return;
    const locations = data?.locations;
    if (!locations?.length) return;
    const loc = locations[locations.length - 1];
    const rawSpeed = loc.coords.speed;
    const speedUnavailable = rawSpeed == null || rawSpeed < 0;
    const speedMph = toMph(rawSpeed);
    const lat = loc.coords.latitude;
    const lng = loc.coords.longitude;
    if (loc.coords.accuracy > 200) return; // Relaxed — urban GPS often reads 80-150m
    // Skip readings where iOS couldn't determine speed — don't let them count as "stopped"
    if (speedUnavailable) return;

    let state = (await loadState()) || {
      phase: 'IDLE', fastCount: 0, slowCount: 0,
      startLat: null, startLng: null, startTime: null, lastLat: null, lastLng: null,
    };

    if (state.phase === 'IDLE') {
      if (speedMph >= MIN_DRIVE_SPEED_MPH) {
        state.fastCount = (state.fastCount || 0) + 1;
        if (state.fastCount >= READINGS_TO_START) {
          state.phase = 'DRIVING';
          state.startLat = lat; state.startLng = lng;
          state.startTime = new Date().toISOString();
          state.fastCount = 0; state.slowCount = 0;
        }
      } else { state.fastCount = 0; }
    } else if (state.phase === 'DRIVING') {
      state.lastLat = lat; state.lastLng = lng;
      if (speedMph < STOP_SPEED_MPH) {
        state.slowCount = (state.slowCount || 0) + 1;
        if (state.slowCount >= READINGS_TO_STOP) {
          await saveTrip(state).catch(() => {}); // best-effort save; always clear state after
          await clearState();
          return;
        }
      } else { state.slowCount = 0; }
    }
    await saveState(state);
  });
}

export async function startDriveDetection(userId) {
  if (!TaskManager) return false; // Native module not in this binary
  const { status } = await Location.getBackgroundPermissionsAsync();
  if (status !== 'granted') return false;
  // Persist user ID so background task can save trips without auth session
  if (userId && AsyncStorage) {
    await AsyncStorage.setItem(USER_ID_KEY, userId);
  }
  // Persist enabled flag so app can revive the task after iOS kills it
  if (AsyncStorage) {
    await AsyncStorage.setItem(ENABLED_KEY, 'true');
  }
  const isRegistered = await TaskManager.isTaskRegisteredAsync(DRIVE_TASK_NAME);
  if (isRegistered) return true;
  setDriveActive(true).catch(() => {});
  await Location.startLocationUpdatesAsync(DRIVE_TASK_NAME, {
    accuracy: Location.Accuracy.Balanced,
    activityType: Location.ActivityType.AutomotiveNavigation,
    timeInterval: 15000,
    distanceInterval: 40,
    showsBackgroundLocationIndicator: true,
    foregroundService: {
      notificationTitle: 'Slate Drive Log',
      notificationBody: 'Tracking mileage for tax deductions',
      notificationColor: '#4F8EF7',
    },
    pausesUpdatesAutomatically: false,
  });
  return true;
}

export async function stopDriveDetection() {
  if (!TaskManager) return;
  if (AsyncStorage) await AsyncStorage.removeItem(ENABLED_KEY);
  const isRegistered = await TaskManager.isTaskRegisteredAsync(DRIVE_TASK_NAME);
  if (isRegistered) await Location.stopLocationUpdatesAsync(DRIVE_TASK_NAME);
  setDriveActive(false).catch(() => {});
  await clearState();
}

// Called on app foreground — silently restarts the task if iOS killed it
export async function reviveDriveDetectionIfNeeded() {
  if (!TaskManager || !AsyncStorage) return;
  try {
    const enabled = await AsyncStorage.getItem(ENABLED_KEY);
    if (enabled !== 'true') return;
    const { status } = await Location.getBackgroundPermissionsAsync();
    if (status !== 'granted') return;
    const isRegistered = await TaskManager.isTaskRegisteredAsync(DRIVE_TASK_NAME);
    if (isRegistered) return; // still alive, nothing to do
    // Task was killed by iOS — restart it silently
    setDriveActive(true).catch(() => {});
    await Location.startLocationUpdatesAsync(DRIVE_TASK_NAME, {
      accuracy: Location.Accuracy.Balanced,
      activityType: Location.ActivityType.AutomotiveNavigation,
      timeInterval: 15000,
      distanceInterval: 40,
      showsBackgroundLocationIndicator: true,
      foregroundService: {
        notificationTitle: 'Slate Drive Log',
        notificationBody: 'Tracking mileage for tax deductions',
        notificationColor: '#4F8EF7',
      },
      pausesUpdatesAutomatically: false,
    });
  } catch (_) {}
}

export async function isDriveDetectionRunning() {
  if (!TaskManager) return false;
  return TaskManager.isTaskRegisteredAsync(DRIVE_TASK_NAME);
}
